import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Compass, Target, TrendingUp, CheckCircle2, Brain } from 'lucide-react';
import { DjayMascot, DjaySpeechBubble } from './DjayMascot';
const STEPS = [{
  n: '1',
  icon: Compass,
  title: 'Plek bepalen',
  desc: 'Een korte intake-toets bepaalt waar je kind nu staat per vak. Geen gokwerk, geen lange instaptoetsen.',
  color: 'orange'
}, {
  n: '2',
  icon: Target,
  title: 'Op niveau oefenen',
  desc: 'Meester djay kiest vragen net buiten wat je kind al kan. Te makkelijk? Iets pittiger. Te moeilijk? Stapje terug met uitleg.',
  color: 'amber'
}, {
  n: '3',
  icon: TrendingUp,
  title: 'Groeien op leerdoelen',
  desc: 'Beheersing per vaardigheid wordt continu bijgehouden. Nieuwe onderwerpen komen vanzelf in beeld via de leerkaart.',
  color: 'emerald'
}];
const COLOR: Record<string, {
  light: string;
  text: string;
  solid: string;
  ring: string;
}> = {
  orange: {
    light: 'bg-orange-100',
    text: 'text-orange-600',
    solid: 'bg-orange-500',
    ring: 'ring-orange-200'
  },
  amber: {
    light: 'bg-amber-100',
    text: 'text-amber-600',
    solid: 'bg-amber-500',
    ring: 'ring-amber-200'
  },
  emerald: {
    light: 'bg-emerald-100',
    text: 'text-emerald-600',
    solid: 'bg-emerald-500',
    ring: 'ring-emerald-200'
  }
};
export function HowItWorksSection() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive(s => (s + 1) % STEPS.length), 3000);
    return () => clearInterval(t);
  }, []);
  return <section id="hoe-het-werkt" className="py-24 lg:py-32 bg-white overflow-hidden" data-id="element-431">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-432">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-433">
          {/* Left: Steps */}
          <div data-id="element-434">
            <motion.div initial={{
            opacity: 0,
            y: 10,
            rotate: -3
          }} whileInView={{
            opacity: 1,
            y: 0,
            rotate: -2
          }} viewport={{
            once: true
          }} className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 border border-orange-200 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-6" data-id="element-435">
              <Compass className="w-3.5 h-3.5" data-id="element-436" />
              Hoe het werkt
            </motion.div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mb-6 tracking-tight" data-id="element-437">
              Hoe werkt het? <br data-id="element-438" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500" data-id="element-439">
                Heel simpel.
              </span>
            </h2>
            <p className="text-lg text-slate-600 font-medium mb-10" data-id="element-440">
              Binnen 2 minuten is je kind aan de slag. Geen ingewikkelde
              instellingen — de engine regelt alles op de achtergrond.
            </p>

            <div className="space-y-4" data-id="element-441">
              {STEPS.map((step, idx) => {
              const c = COLOR[step.color];
              const Icon = step.icon;
              const isActive = idx === active;
              return <motion.button key={idx} onClick={() => setActive(idx)} whileHover={{
                x: 4
              }} className={`w-full text-left flex gap-5 p-4 rounded-2xl border-2 transition-all ${isActive ? `bg-white ${c.ring} ring-2 border-transparent shadow-md` : 'border-transparent hover:bg-slate-50'}`} data-id="element-442">
                    <div className="flex-shrink-0" data-id="element-443">
                      <motion.div animate={isActive ? {
                    scale: [1, 1.08, 1]
                  } : {
                    scale: 1
                  }} transition={{
                    duration: 1.2,
                    repeat: isActive ? Infinity : 0
                  }} className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm transition-colors ${isActive ? `${c.solid} text-white` : `${c.light} ${c.text}`}`} data-id="element-444">
                        <Icon className="w-6 h-6" strokeWidth={2.5} data-id="element-445" />
                      </motion.div>
                    </div>
                    <div className="flex-1" data-id="element-446">
                      <div className="flex items-baseline gap-2 mb-1" data-id="element-447">
                        <span className={`text-[11px] font-black uppercase tracking-wider ${c.text}`} data-id="element-448">
                          Stap {step.n}
                        </span>
                        <h3 className="text-lg font-black text-slate-900" data-id="element-449">
                          {step.title}
                        </h3>
                      </div>
                      <p className="text-sm text-slate-600 font-medium leading-relaxed" data-id="element-450">
                        {step.desc}
                      </p>
                    </div>
                  </motion.button>;
            })}
            </div>

            <div className="mt-10" data-id="element-451">
              <a href="#aanmelden" className="inline-flex justify-center items-center gap-2 bg-orange-500 text-white shadow-[0_4px_0_0_rgb(194,65,12)] hover:bg-orange-400 hover:-translate-y-0.5 active:translate-y-[2px] active:shadow-none rounded-2xl font-bold px-8 py-4 transition-all text-lg" data-id="element-452">
                Start met oefenen
                <ArrowRight className="w-5 h-5" data-id="element-453" />
              </a>
            </div>
          </div>

          {/* Right: Animated phone with mascot guiding through current step */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-454">
            <div className="absolute -inset-8 bg-orange-200/30 blur-3xl rounded-full -z-10" data-id="element-455" />

            {/* Phone frame */}
            <div className="relative max-w-[300px] mx-auto bg-slate-900 rounded-[2.5rem] p-3 shadow-2xl" data-id="element-456">
              <div className="bg-[#FFF9F2] rounded-[2rem] overflow-hidden aspect-[9/16] relative" data-id="element-457">
                {/* Status bar */}
                <div className="h-7 bg-[#FFF9F2] flex items-center justify-between px-6 text-[10px] font-bold text-slate-600" data-id="element-458">
                  <span data-id="element-459">9:41</span>
                  <span className="flex gap-1" data-id="element-460">●●●</span>
                </div>

                {/* Mascot row */}
                <div className="px-5 py-3 flex items-center gap-3 border-b border-orange-100" data-id="element-461">
                  <DjayMascot size={42} data-id="element-462" />
                  <AnimatePresence mode="wait" data-id="element-463">
                    <motion.div key={active} initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: 10
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-464">
                      <DjaySpeechBubble color="orange" data-id="element-465">
                        {active === 0 && 'Even kijken waar je staat!'}
                        {active === 1 && 'Deze is op jouw niveau.'}
                        {active === 2 && 'Mooi, dit doel beheers je nu!'}
                      </DjaySpeechBubble>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Content area changes per step */}
                <div className="p-5" data-id="element-466">
                  <AnimatePresence mode="wait" data-id="element-467">
                    <motion.div key={active} initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -20
                  }} transition={{
                    duration: 0.35
                  }} data-id="element-468">
                      {active === 0 && <IntakeFrame data-id="element-469" />}
                      {active === 1 && <PracticeFrame data-id="element-470" />}
                      {active === 2 && <MasteryFrame data-id="element-471" />}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* Step pips */}
            <div className="flex justify-center gap-2 mt-6" data-id="element-472">
              {STEPS.map((_, i) => <motion.button key={i} onClick={() => setActive(i)} animate={{
              width: i === active ? 28 : 8,
              backgroundColor: i === active ? '#F97316' : '#E2E8F0'
            }} className="h-2 rounded-full" aria-label={`Stap ${i + 1}`} data-id="element-473" />)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function IntakeFrame() {
  return <div data-id="element-474">
      <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2" data-id="element-475">
        Intake-toets
      </div>
      <div className="space-y-2" data-id="element-476">
        {[{
        label: 'Rekenen',
        done: true,
        val: 68
      }, {
        label: 'Taal',
        done: true,
        val: 74
      }, {
        label: 'Spelling',
        val: 45,
        active: true
      }, {
        label: 'Topografie',
        val: 0
      }].map((row, i) => <motion.div key={row.label} initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: i * 0.1
      }} className="flex items-center gap-2" data-id="element-477">
            <div className="text-[11px] font-black text-slate-700 w-20" data-id="element-478">
              {row.label}
            </div>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-479">
              <motion.div initial={{
            width: 0
          }} animate={{
            width: `${row.val}%`
          }} transition={{
            delay: 0.2 + i * 0.1,
            duration: 0.8
          }} className={`h-full rounded-full ${row.done ? 'bg-emerald-500' : row.active ? 'bg-orange-500' : 'bg-slate-300'}`} data-id="element-480" />
            </div>
            <div className="text-[10px] font-black text-slate-500 w-8 text-right" data-id="element-481">
              {row.val}%
            </div>
          </motion.div>)}
      </div>
    </div>;
}
function PracticeFrame() {
  return <div data-id="element-482">
      <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2" data-id="element-483">
        Vraag 3 / 8 · Tier 7
      </div>
      <div className="text-sm font-black text-slate-800 mb-3" data-id="element-484">
        Hoeveel is 3/8 + 1/8?
      </div>
      <div className="space-y-1.5" data-id="element-485">
        {[{
        label: '4/16',
        correct: false
      }, {
        label: '4/8',
        correct: true,
        picked: true
      }, {
        label: '2/8',
        correct: false
      }].map((opt, i) => <motion.div key={opt.label} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.2 + i * 0.08
      }} className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 border text-xs font-black ${opt.picked ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-500'}`} data-id="element-486">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black ${opt.picked ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-487">
              {String.fromCharCode(65 + i)}
            </div>
            <span data-id="element-488">{opt.label}</span>
            {opt.picked && <CheckCircle2 className="w-3 h-3 ml-auto text-emerald-600" data-id="element-489" />}
          </motion.div>)}
      </div>
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.8
    }} className="mt-3 flex items-center gap-1.5 text-[9px] font-bold text-slate-500" data-id="element-490">
        <Brain className="w-2.5 h-2.5 text-orange-500" data-id="element-491" />
        <span data-id="element-492">Aangepast aan jouw niveau</span>
      </motion.div>
    </div>;
}
function MasteryFrame() {
  return <div data-id="element-493">
      <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2" data-id="element-494">
        Voortgang
      </div>
      <div className="flex items-center gap-3 mb-3" data-id="element-495">
        <div className="relative w-14 h-14" data-id="element-496">
          <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56" data-id="element-497">
            <circle cx="28" cy="28" r="22" fill="none" stroke="#F1F5F9" strokeWidth="5" data-id="element-498" />
            <motion.circle cx="28" cy="28" r="22" fill="none" stroke="#10B981" strokeWidth="5" strokeLinecap="round" strokeDasharray={2 * Math.PI * 22} initial={{
            strokeDashoffset: 2 * Math.PI * 22
          }} animate={{
            strokeDashoffset: 2 * Math.PI * 22 * 0.18
          }} transition={{
            duration: 1.2
          }} data-id="element-499" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-[11px] font-black text-emerald-700" data-id="element-500">
            82%
          </div>
        </div>
        <div data-id="element-501">
          <div className="text-xs font-black text-slate-800 leading-tight" data-id="element-502">
            Breuken optellen
          </div>
          <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider" data-id="element-503">
            ✓ Beheerst
          </div>
        </div>
      </div>
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.6
    }} className="bg-amber-50 border border-amber-200 rounded-lg p-2" data-id="element-504">
        <div className="text-[9px] font-black text-amber-700 uppercase tracking-wider mb-0.5" data-id="element-505">
          ⚡ Nieuw ontgrendeld
        </div>
        <div className="text-[11px] font-black text-amber-900" data-id="element-506">
          Breuken vermenigvuldigen
        </div>
      </motion.div>
    </div>;
}